import { Module, GetterTree, ActionTree, MutationTree } from 'vuex';
import http from '@/http';

const state: RiskProfileState = {
  searchOptions: { page: 1, itemsPerPage: 10, query: '' },
  riskProfile: {
    name: '',
    description: '',
  },
  newRiskProfile: {
    name: '',
    custom_job_role_name: '',
    description: '',
    status: '',
    anzsco_oid: '',
    rpt_oid: '',
    business_oid: '',
    extra_question_groups: [],
    risk_profile_items: [],
  },
  extraQuestionGroups: [],
  bodyParts: [],
  questionGroups: [],
  riskProfileItemsBodyParts: {},
  riskProfileItemsQuestionGroups: {},
  generalHealthChecks: [],
  list: null,
};

const getters: GetterTree<RiskProfileState, RootState> = {
  riskProfile(state): Nullable<Model> {
    const { riskProfile } = state;
    return riskProfile;
  },
  bodyParts(state): Model[] {
    const { bodyParts } = state;
    return bodyParts;
  },
  // Use in the creating/updating risk profile process to filter out and map the enabled risk profile items from the
  // getter, riskProfileTemplateItems, in the risk profile template module.
  enabledRiskProfileItems: (state, getters, rootState, rootGetters) => {
    if (rootGetters['RiskProfileTemplate/riskProfileTemplateItems']) {
      return (
        rootGetters['RiskProfileTemplate/riskProfileTemplateItems']
          .filter((item: RiskProfileItem) => item.edge_is_enabled)
          .map((item: RiskProfileItem) => {
            return {
              object_id: item.object_id,
              edge_is_enabled: item.edge_is_enabled,
              edge_how_often: item.edge_how_often,
              edge_extform_value: item.edge_extform_value,
              title: item.title,
            };
          }) || []
      );
    }
    return [];
  },
  // deprecated
  bodyPartsRiskProfileItemsMap(state, getters): object {
    const enabledRPIOids = getters.enabledRiskProfileItems.map(
      (item: { object_id: string }) => item.object_id
    );
    const bodyPartsRiskProfileItemsMap: object = {};
    for (const enabledRPIOid of enabledRPIOids) {
      if (!state.riskProfileItemsBodyParts[enabledRPIOid]) {
        continue;
      }

      const bpOids: string[] =
        Object.keys(state.riskProfileItemsBodyParts[enabledRPIOid]) || '';

      const bodyParts: BodyPart[] =
        state.bodyParts.filter((bp: BodyPart) =>
          bp.object_id ? bpOids.includes(bp.object_id) : false
        ) || [];

      for (const bodyPart of bodyParts) {
        if (!bodyPart.object_id) {
          continue;
        }
        if (bodyPart.is_symmetrical) {
          const leftKey = bodyPart.key + '_left';
          const rightKey = bodyPart.key + '_right';
          if (
            Object.prototype.hasOwnProperty.call(
              bodyPartsRiskProfileItemsMap,
              leftKey
            ) &&
            Object.prototype.hasOwnProperty.call(
              bodyPartsRiskProfileItemsMap,
              rightKey
            )
          ) {
            bodyPartsRiskProfileItemsMap[leftKey][enabledRPIOid] =
              state.riskProfileItemsBodyParts[enabledRPIOid][
                bodyPart.object_id
              ];
            bodyPartsRiskProfileItemsMap[rightKey] =
              bodyPartsRiskProfileItemsMap[leftKey];
          } else {
            bodyPartsRiskProfileItemsMap[leftKey] = {
              [enabledRPIOid]:
                state.riskProfileItemsBodyParts[enabledRPIOid][
                  bodyPart.object_id
                ],
            };
            bodyPartsRiskProfileItemsMap[rightKey] =
              bodyPartsRiskProfileItemsMap[leftKey];
          }
        } else {
          if (
            Object.prototype.hasOwnProperty.call(
              bodyPartsRiskProfileItemsMap,
              bodyPart.key
            )
          ) {
            bodyPartsRiskProfileItemsMap[bodyPart.key][enabledRPIOid] =
              state.riskProfileItemsBodyParts[enabledRPIOid][
                bodyPart.object_id
              ];
          } else {
            bodyPartsRiskProfileItemsMap[bodyPart.key] = {
              [enabledRPIOid]:
                state.riskProfileItemsBodyParts[enabledRPIOid][
                  bodyPart.object_id
                ],
            };
          }
        }
      }
    }
    return bodyPartsRiskProfileItemsMap;
  },
  // deprecated
  questionGroupRiskProfileItems(state, getters): Record<string, string[]> {
    const enabledRPIOids = getters.enabledRiskProfileItems.map(
      (item: { object_id: string }) => item.object_id
    );
    const questionGroupRiskProfileItems: Record<string, string[]> = {};
    for (const enabledRPIOid of enabledRPIOids) {
      const questionGroupOids: [] =
        state.riskProfileItemsQuestionGroups[enabledRPIOid] || [];
      for (const qgOid of questionGroupOids) {
        if (
          Object.prototype.hasOwnProperty.call(
            questionGroupRiskProfileItems,
            qgOid
          )
        ) {
          const rpiOids: string[] = questionGroupRiskProfileItems[qgOid];
          rpiOids.push(enabledRPIOid);
          questionGroupRiskProfileItems[qgOid] = [...new Set(rpiOids)];
        } else {
          const rpiOids: string[] = [];
          rpiOids.push(enabledRPIOid);
          questionGroupRiskProfileItems[qgOid] = [...new Set(rpiOids)];
        }
      }
    }
    return questionGroupRiskProfileItems;
  },
  extraQuestionGroups(state): string[] {
    const { extraQuestionGroups } = state;
    return extraQuestionGroups;
  },
  // Use in the creating/updating risk profile process to filter out the selected extra question groups and the
  // question groups that are related to selected risk profile items, which are from
  // RiskProfileTemplate/riskProfileTemplateItems
  enabledQuestionGroups(state, getters) {
    const enabledQuestionGroupOids = Object.keys(
      getters.questionGroupRiskProfileItems
    );
    const enabledQuestionGroups =
      state.questionGroups.filter(
        (item: Model) =>
          item.object_id && enabledQuestionGroupOids.includes(item.object_id)
      ) || [];
    const extraQuestionGroups =
      state.extraQuestionGroups
        .map(
          (object_id: string) =>
            state.questionGroups.find(
              (qg: Model) => qg.object_id === object_id
            ) || null
        )
        .filter((n) => n) || [];
    return enabledQuestionGroups.concat(extraQuestionGroups);
  },
  // filter out the question groups that could be optional selections for the business
  availableQuestionGroups(state, getters) {
    const enabledQuestionGroupOids = Object.keys(
      getters.questionGroupRiskProfileItems
    );
    return (
      state.questionGroups.filter(
        (item: Model) =>
          item.object_id && !enabledQuestionGroupOids.includes(item.object_id)
      ) || []
    );
  },
  // map the risk profile items from RiskProfileTemplate/riskProfileTemplateItems with related body parts and
  // calculate the severity
  bodyPartPreviewItems: (state, getters, rootState, rootGetters) => {
    const dataMap = getters.bodyPartsRiskProfileItemsMap;
    return Object.keys(dataMap).map((bpKey) => {
      const rpiOids = dataMap[bpKey];
      const severities: number[] = [];
      const riskProfileItems: string[] = [];
      for (const rpiOid in rpiOids) {
        const riskProfileItem = rootGetters[
          'RiskProfileTemplate/riskProfileTemplateItems'
        ]
          .filter((item) => item.object_id === rpiOid)
          .shift();
        if (riskProfileItem) {
          const severity = riskProfileItem.edge_how_often * rpiOids[rpiOid];
          const formattedSeverity =
            Math.round((severity + Number.EPSILON) * 100) / 100;
          severities.push(formattedSeverity);
          riskProfileItems.push(riskProfileItem.title);
        }
      }
      let maxSeverity = Math.max.apply(null, severities);
      console.debug(maxSeverity);
      // maxSeverity = (1 / (1 + Math.exp(-20 * maxSeverity)) - 0.5) * 2
      maxSeverity = (1 / (1 + Math.pow(10, -2 * maxSeverity)) - 0.5) * 2;
      maxSeverity = Math.round(maxSeverity * 100) / 100;
      return {
        id: bpKey,
        severity: maxSeverity,
        riskProfileItems,
      };
    });
  },
};

const actions: ActionTree<RiskProfileState, RootState> = {
  fetch({ commit }, { object_id, params }) {
    return http.v1.rp.fetch(object_id, { params }).then(({ data }) => {
      if (data.data) {
        commit('SET_RISK_PROFILE', data.data);

        const tree = data.data.risk_profile_tree.map((item) => {
          item.title = item.custom_job_role_name || item.name;
          delete item.custom_job_role_name;
          return item;
        });
        commit('RiskProfileTemplate/SET_RISK_PROFILE_TREE', tree, {
          root: true,
        });
      }
    });
  },
  create({ commit }, payload) {
    return http.v1.rp
      .create(payload)
      .then(({ data }) => {
        // console.log(data)
        if (data.data) {
          commit('SET_RISK_PROFILE', data.data);
          return true;
        }
      })
      .catch(() => {
        return false;
      });
  },
  createForPreview({ commit }, payload) {
    return http.v1.rp.createForPreview(payload).then((response) => {
      if (response.data.data) {
        commit('SET_RISK_PROFILE', response.data.data);
      }
      return response;
    });
  },
  updateForPreview({ commit }, payload) {
    // Update for preview risk profile uses same update API as normal risk profile
    return http.v1.rp
      .updateForPreview(payload.object_id, payload)
      .then((response) => {
        if (response.data?.data) {
          commit('SET_RISK_PROFILE', response.data.data);
        }
        return response;
      });
  },
  update(_, payload) {
    return http.v1.rp.update(payload.object_id, payload);
  },
  searchForName(_, options) {
    if (options.params.search_name.length === 0) {
      return null;
    }

    return http.v1.rp.verifyName(options);
  },
  fetchFullStructure({ commit }, oid) {
    return http.v1.rp.fetchFullStructure(oid).then(({ data }) => {
      const payload = data.data;
      commit('SET_RISK_PROFILE', payload.risk_profile);
      const tree = payload.risk_profile_tree.map((item) => {
        item.title = item.custom_job_role_name || item.name;
        delete item.custom_job_role_name;
        return item;
      });
      commit('RiskProfileTemplate/SET_RISK_PROFILE_TREE', tree, { root: true });
      commit('SET_RPI_BP_MAP', payload.rpi_map_bp);
      commit('SET_RPI_QG_MAP', payload.rpi_map_qg);
      commit('SET_BODY_PARTS', payload.body_parts);
      commit('SET_QUESTION_GROUPS', payload.question_groups);
      commit('SET_EXTRA_QUESTION_GROUPS', payload.extra_question_groups);
    });
  },
  fetchList({ commit }, options) {
    return http.v1.rp.fetchList(options).then(({ data }) => {
      const payload = data;
      const items = payload.data.data;
      commit('SET_STATE', {
        key: 'list',
        payload: {
          items,
          total: payload.data.total,
        },
      });
      return {
        items,
        total: payload.data.total,
      };
    });
  },
  duplicate(_, oid) {
    return http.v1.rp.duplicate(oid);
  },
  deleteForPreview(_, oid) {
    return http.v1.rp.deleteForPreview(oid);
  },
  delete(_, oid) {
    return http.v1.rp.delete(oid);
  },
  preFetchGHC({ commit }, options) {
    return http.v1.rp.preFetchGHC(options).then(({ data }) => {
      const payload = data.data;
      commit('SET_QUESTION_GROUPS', payload.question_groups);
      commit('SET_STATE', {
        key: 'generalHealthChecks',
        payload: payload.general_health_checks,
      });
    });
  },
};

const mutations: MutationTree<RiskProfileState> = {
  SET_STATE(state, { key, payload }) {
    state[key] = payload;
  },
  CLEAR_STATE(state) {
    (state.searchOptions = { page: 1, itemsPerPage: 10, query: '' }),
      (state.riskProfile = {
        name: '',
        description: '',
      });
    state.riskProfile = {
      name: '',
      description: '',
    };
    state.newRiskProfile = {
      name: '',
      custom_job_role_name: '',
      description: '',
      status: '',
      anzsco_oid: '',
      rpt_oid: '',
      business_oid: '',
      extra_question_groups: [],
      risk_profile_items: [],
    };
    state.extraQuestionGroups = [];
    state.bodyParts = [];
    state.questionGroups = [];
    state.riskProfileItemsBodyParts = {};
    state.riskProfileItemsQuestionGroups = {};
  },
  SET_SEARCH_OPTIONS(state, payload) {
    state.searchOptions = payload;
  },
  SET_RISK_PROFILE(state, payload) {
    state.riskProfile = payload;
  },
  SET_NEW_RISK_PROFILE(state, payload) {
    state.newRiskProfile = payload;
  },
  SET_BODY_PARTS(state, payload) {
    state.bodyParts = payload;
  },
  SET_RPI_BP_MAP(state, payload) {
    state.riskProfileItemsBodyParts = payload;
  },
  SET_RPI_QG_MAP(state, payload) {
    state.riskProfileItemsQuestionGroups = payload;
  },
  SET_QUESTION_GROUPS(state, payload) {
    // add description as content
    payload.map((item) => {
      item.content = item.description;
    });
    state.questionGroups = payload;
  },
  SET_EXTRA_QUESTION_GROUPS(state, payload) {
    state.extraQuestionGroups = payload;
  },
};

const riskProfile: Module<RiskProfileState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export default riskProfile;
